/**
 * Formats a timestamp as a relative time string.
 * Examples (es): "Ahora", "Hace 5m", "Hace 2h", "Hace 3d", or a date like "15 ene"
 * Examples (en): "Now", "5m ago", "2h ago", "3d ago", or a date like "Jan 15"
 */
export type Locale = "es" | "en";

export function formatTimestamp(
  timestamp: string | null | undefined,
  locale: Locale = "es"
): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const isSpanish = locale === "es";

  if (diffMins < 1) return isSpanish ? "Ahora" : "Now";
  if (diffMins < 60) return isSpanish ? `Hace ${diffMins}m` : `${diffMins}m ago`;
  if (diffHours < 24)
    return isSpanish ? `Hace ${diffHours}h` : `${diffHours}h ago`;
  if (diffDays < 7) return isSpanish ? `Hace ${diffDays}d` : `${diffDays}d ago`;

  return date.toLocaleDateString(isSpanish ? "es-ES" : "en-US", {
    day: "numeric",
    month: "short",
  });
}

/** Relative label for chat bubbles; `title` holds the full datetime for tooltips. */
export function formatMessageTimestamp(
  timestamp: string,
  locale: Locale = "es"
): { display: string; title: string } {
  const date = new Date(timestamp);
  const isSpanish = locale === "es";
  return {
    display: formatTimestamp(timestamp, locale),
    title: date.toLocaleString(isSpanish ? "es-ES" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Label for date separators in chat (Hoy / Ayer / formatted date). */
export function formatChatDateSeparator(
  timestamp: string,
  locale: Locale = "es"
): string {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isSpanish = locale === "es";

  if (isSameCalendarDay(date, now)) {
    return isSpanish ? "Hoy" : "Today";
  }
  if (isSameCalendarDay(date, yesterday)) {
    return isSpanish ? "Ayer" : "Yesterday";
  }

  return date.toLocaleDateString(isSpanish ? "es-ES" : "en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

